import { ChevronRightIcon } from "@chakra-ui/icons";
import {
  Flex,
  Text,
  Accordion,
  AccordionItem,
  AccordionButton,
  AccordionPanel,
  Tag,
  TagLabel,
  TagRightIcon,
  useDisclosure,
} from "@chakra-ui/react";
import { formatStringWithCommas } from "@syla/shared/helpers/formatting";
import { DataSourceType } from "@syla/shared/types/models/DataSourceBase";
import { PopulatedWallet } from "@syla/shared/types/models/WalletBase";
import React, { useMemo } from "react";
import { NumberStyler } from "../../../../helper/NumberStyler";

import { sortAssetHoldings } from "../../../../helper/wallet/sortAssetHoldings";
import { walletStateProps } from "../../../../helper/walletProps";
import { useModeDisclosure } from "../../../../hooks/useModeDisclosure";
import { useNavigator, Route } from "../../../../routers/navigator";
import { reSyncWallets } from "../../../../store/actions/reSyncWallets";
import { useCurrentAccountStore } from "../../../../store/currentAccountStore";
import { useValueHoldings } from "../../../../store/useValueHoldings";
import { scrollbarStyles } from "../../../../theme/scrollbarStyles";
import { ImageWithMissingSrc } from "../../../atoms/ImageWithMissingSrc";
import { RenderIcons } from "../../../atoms/RenderIcons";
import { SkeletonRect } from "../../../atoms/Skeletons";
import { DUMMY_HOLDINGS } from "../dummyData";
import { getDataSourceStatusProps } from "../getDataSourceStatusProps";
import { LastSyncedTime } from "../LastSyncedTime";
import { DataSourceExpanded } from "./dataSourceExpanded/DataSourceExpanded";
import { DataSourceMenuButton } from "./DataSourceMenuButton";
import { DeleteWalletModal } from "./DeleteWalletModal";
import {
  EditDataSourceDrawer,
  EditDataSourceMode,
} from "./EditDataSourceDrawer";
import { getWalletHoldingsValue } from "./getWalletHoldingsValue";

export const MainDataSourceTableRow = ({
  walletResponse,
}: {
  walletResponse: PopulatedWallet;
}): React.ReactElement => {
  const accountId = useCurrentAccountStore(({ accountId }) => accountId);
  const navigate = useNavigator();

  const { txCountLoading, holdingsLoading, syncInProgress } =
    walletStateProps(walletResponse);

  const {
    isOpen: isDrawerOpen,
    mode: drawerMode,
    onOpen: openDrawer,
    onClose: closeDrawer,
  } = useModeDisclosure<EditDataSourceMode>("edit");

  const deleteWalletDisclosure = useDisclosure();

  const walletName =
    walletResponse.customName || walletResponse.dataSource.name || "wallet";

  // boolean if this wallet was imported via api
  const allowApiSync = walletResponse.allowSync;

  // re-sync wallet handler
  const reSyncWalletHandler = async () => {
    // check if the wallet was imported via api
    if (!allowApiSync) return;

    reSyncWallets(accountId, [walletResponse]);
  };

  const statusProps = getDataSourceStatusProps(walletResponse);
  const { data: holdingsValue, isLoading: holdingsValueLoading } =
    useValueHoldings();

  const totalHoldingValue = useMemo(
    () =>
      getWalletHoldingsValue({
        walletWithHoldings: walletResponse,
        holdingsValue,
      }),
    [holdingsValue, walletResponse],
  );

  return (
    <>
      <Accordion allowToggle w="full">
        <AccordionItem
          w="full"
          shadow="sm"
          bgColor="white"
          borderWidth="1px"
          borderColor="gray.300"
          borderRadius="md"
          overflow="hidden"
        >
          {/* ------------------------ DataSource Row ------------------------*/}
          <Flex w="full" borderBottomWidth="1px" borderColor="gray.300">
            <AccordionButton
              flex="1"
              p={0}
              minW={0}
              _hover={{ backgroundColor: "gray.50" }}
              _active={{ backgroundColor: "gray.50" }}
            >
              <Flex
                flex={1}
                overflowX="auto"
                css={scrollbarStyles.none}
                height="full"
                justifyContent="flex-start"
                alignItems="center"
                gap={5}
                paddingY={2}
                paddingX={4}
              >
                {/* ------------ Icon -----------------*/}
                <ImageWithMissingSrc
                  src={walletResponse.dataSource.image}
                  alt=""
                  minW="3rem"
                  maxW="3rem"
                  noPaddingIcon
                />

                <Flex
                  justifyContent="space-between"
                  gap={5}
                  alignItems="center"
                  height="full"
                  flex="1"
                >
                  {/* ------------ Data Source Details ------------ */}
                  <Flex
                    minW="20rem"
                    maxW="20rem"
                    w="20rem"
                    direction="column"
                    alignItems="flex-start"
                    flex="1"
                  >
                    <Flex
                      direction="row"
                      alignItems="center"
                      gap={2}
                      w="full"
                      minW="0"
                    >
                      <Text isTruncated>
                        {walletResponse?.customName ||
                          walletResponse.dataSource?.name}
                      </Text>
                      {/* only show the green tick if the api data source has been synced in the last 24 hours */}
                      {statusProps.icon}
                    </Flex>
                    <Flex gap={1}>
                      {
                        <Text color={statusProps.color} fontSize="xs">
                          {statusProps.status ?? statusProps.importMethodName}
                        </Text>
                      }
                      <LastSyncedTime
                        walletResponse={walletResponse}
                        lastUpdated={statusProps.lastUpdated}
                        whiteSpace="nowrap"
                        fontSize="xs"
                      />
                    </Flex>
                  </Flex>

                  {/* ------------ Transaction Number column -----------------*/}
                  <Flex justifyContent="flex-end" minW="7.5rem">
                    <SkeletonRect isLoaded={!txCountLoading}>
                      <Tag
                        px={3}
                        py={1}
                        borderRadius="full"
                        bg="gray.100"
                        fontWeight="medium"
                        fontSize="md"
                        _hover={{ bg: "gray.200" }}
                        shadow="none"
                        cursor="pointer"
                        whiteSpace="nowrap"
                        onClick={() =>
                          // navigate to the transactions screen with the walletId as a location prop
                          navigate({
                            route: Route.Transactions,
                            accountId,
                            state: {
                              filter: {
                                dataSource: [walletResponse._id],
                              },
                            },
                          })
                        }
                      >
                        <TagLabel color="gray.900">
                          {formatStringWithCommas(
                            (walletResponse.transactionCount || 0).toString(),
                          )}
                        </TagLabel>
                        <TagRightIcon color="gray.600" as={ChevronRightIcon} />
                      </Tag>
                    </SkeletonRect>
                  </Flex>
                  {/* ------------ Token icons column -----------------*/}
                  <Flex minW="13rem">
                    <RenderIcons
                      assets={sortAssetHoldings(
                        walletResponse.holdings ?? DUMMY_HOLDINGS,
                      ).map((holding) => holding.asset)}
                      qty={3}
                      isLoading={holdingsLoading}
                    />
                  </Flex>
                  {/* ------------ Market Value column -----------------*/}
                  <SkeletonRect
                    isLoaded={!(holdingsValueLoading || holdingsLoading)}
                  >
                    <Flex minW="6rem" justify="flex-end">
                      <NumberStyler num={totalHoldingValue} unit="currency" />
                    </Flex>
                  </SkeletonRect>
                </Flex>
              </Flex>
            </AccordionButton>

            {/* ------------ 3 Dots Menu column -----------------*/}
            <DataSourceMenuButton
              canSync={allowApiSync}
              isSyncDisabled={syncInProgress}
              isEditDisabled={syncInProgress}
              isFileImportDisabled={syncInProgress}
              isDeleteDisabled={syncInProgress}
              onSync={reSyncWalletHandler}
              onEdit={() => {
                openDrawer("edit");
              }}
              onFileImport={() => {
                openDrawer("fileImport");
              }}
              onDelete={deleteWalletDisclosure.onOpen}
              walletAddress={
                (
                  [DataSourceType.Blockchain, DataSourceType.Wallet].includes(
                    walletResponse.dataSource.type,
                  )
                ) ?
                  walletResponse.apiKey
                : undefined
              }
            />
            {deleteWalletDisclosure.isOpen && (
              <DeleteWalletModal
                accountId={accountId}
                walletId={walletResponse._id}
                walletName={walletName}
                disclosure={deleteWalletDisclosure}
              />
            )}
          </Flex>
          {/* ------------------ DataSource Expanded section ------------------------*/}
          <AccordionPanel p={0} w="full">
            <DataSourceExpanded
              walletResponse={walletResponse}
              openEditDrawer={() => openDrawer("edit")}
            />
          </AccordionPanel>
        </AccordionItem>
      </Accordion>

      {/* ------------------ Edit Data Source Drawer ------------------ */}
      <EditDataSourceDrawer
        isOpen={isDrawerOpen}
        onClose={closeDrawer}
        wallet={walletResponse}
        editMode={drawerMode}
      />
    </>
  );
};
